import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Search, Send, Link2, Mail, Phone, CheckCircle2, XCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LogoImg } from "@/components/site/Logo";
import { CONTENT } from "@/lib/i18n";
import { api, errCode } from "@/lib/api";
import { useAdminTitle } from "./AdminLogin";

const STATUS = {
  new: ["Yeni", "bg-sky-100 text-sky-900"],
  qualified: ["Uygun", "bg-emerald-100 text-emerald-900"],
  follow_up: ["Takip gerekli", "bg-amber-100 text-amber-900"],
  not_suitable: ["Uygun değil", "bg-stone-200 text-stone-800"],
  booked: ["Görüşme planlandı", "bg-violet-100 text-violet-900"],
  converted: ["Ücretli danışana dönüştü", "bg-forest text-ivory"],
};
const A = CONTENT.tr.apply;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Badge = ({ s }) => <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${STATUS[s]?.[1]}`}>{STATUS[s]?.[0]}</span>;

const BookingSettings = () => {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setUrl(data?.booking_url ?? "")).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { booking_url: url }); toast.success("Randevu bağlantısı kaydedildi."); }
    catch { toast.error("Geçerli bir https:// bağlantısı girin."); }
    setSaving(false);
  };
  return (
    <div className="rounded-2xl border border-forest/10 bg-white p-5" data-testid="booking-settings">
      <p className="flex items-center gap-2 text-sm font-semibold text-forest"><Link2 size={16} /> Randevu bağlantısı (Calendly / Google Takvim)</p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <input data-testid="booking-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://calendly.com/..." className="flex-1 rounded-xl border border-forest/20 px-4 py-2.5 text-sm outline-none focus:border-gold-deep" />
        <button onClick={save} disabled={saving} data-testid="booking-url-save" className="btn btn-forest !py-2.5 text-sm">{saving && <Loader2 size={14} className="animate-spin" />} Kaydet</button>
      </div>
      <p className="mt-2 text-xs text-forest/65">Davet e-postalarındaki “Görüşme zamanını seçin” düğmesi bu bağlantıyı kullanır.</p>
    </div>
  );
};

const Row = ({ k, children }) => (
  <div className="border-b border-forest/10 py-3">
    <dt className="text-xs uppercase tracking-[0.14em] text-gold-text">{k}</dt>
    <dd className="mt-1 whitespace-pre-wrap text-sm text-forest">{children || "—"}</dd>
  </div>
);

const Detail = ({ app, onChange }) => {
  const [notes, setNotes] = useState(app.notes || "");
  const [busy, setBusy] = useState("");
  useEffect(() => { setNotes(app.notes || ""); }, [app.id, app.notes]);
  const patch = async (body, key) => {
    setBusy(key);
    try { const { data } = await api.patch(`/admin/applications/${app.id}`, body); onChange(data); toast.success("Kaydedildi."); }
    catch { toast.error("Kaydedilemedi."); }
    setBusy("");
  };
  const invite = async () => {
    setBusy("invite");
    try { const { data } = await api.post(`/admin/applications/${app.id}/send-invitation`); onChange(data); toast.success("Randevu daveti gönderildi."); }
    catch (e) {
      const c = errCode(e);
      toast.error(c === "booking_url_missing" ? "Önce randevu bağlantısını kaydedin." : `E-posta gönderilemedi (${c || "hata"}).`);
    }
    setBusy("");
  };
  return (
    <div className="space-y-6" data-testid="application-detail">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm font-medium text-forest">Durum
          <select data-testid="detail-status-select" value={app.status} disabled={busy === "status"} onChange={(e) => patch({ status: e.target.value }, "status")} className="mt-1 w-full rounded-xl border border-forest/20 bg-white px-3 py-2.5 text-sm">
            {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <div className="flex items-end">
          <button onClick={invite} disabled={busy === "invite"} data-testid="detail-send-invitation" className="btn btn-gold w-full !py-3 text-sm">
            {busy === "invite" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Randevu daveti gönder
          </button>
        </div>
      </div>
      {app.invitation_sent_at && <p className="text-xs text-forest/70" data-testid="detail-invitation-sent">Davet gönderildi: {fmt(app.invitation_sent_at)}</p>}
      <dl>
        <Row k="E-posta"><a className="underline" href={`mailto:${app.email}`}>{app.email}</a></Row>
        <Row k="Telefon / WhatsApp">{app.phone}</Row>
        <Row k="Tercih edilen iletişim">{A.methods[app.contact_method]}</Row>
        <Row k="Durum">{A.location[app.location]}</Row>
        <Row k="İhtiyaçlar">{app.needs.map((n) => A.needs[n]).join(", ")}{app.needs_other ? ` — ${app.needs_other}` : ""}</Row>
        <Row k="Aciliyet">{A.urgency[app.urgency]}</Row>
        <Row k="Ödeme yapmaya açık mı?">{A.pay[app.willing_to_pay]}</Row>
        <Row k="Açıklama">{app.situation}</Row>
        <Row k="Form dili">{app.lang === "tr" ? "Türkçe" : "Slovence"}</Row>
        <Row k="Başvuru tarihi">{fmt(app.created_at)}</Row>
      </dl>
      <label className="block text-sm font-medium text-forest">Özel notlar
        <textarea data-testid="detail-notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-xl border border-forest/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-gold-deep" />
      </label>
      <button onClick={() => patch({ notes }, "notes")} disabled={busy === "notes"} data-testid="detail-notes-save" className="btn btn-outline !py-2.5 text-sm">Notu kaydet</button>
      <div>
        <p className="text-xs uppercase tracking-[0.14em] text-gold-text">E-posta geçmişi</p>
        <ul className="mt-2 space-y-1.5" data-testid="detail-email-log">
          {(app.email_log || []).length === 0 && <li className="text-sm text-forest/60">Kayıt yok.</li>}
          {(app.email_log || []).map((l, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-forest">
              {l.ok ? <CheckCircle2 size={14} className="text-emerald-700" /> : <XCircle size={14} className="text-red-700" />}
              {{ confirmation: "Başvuru onayı", admin_alert: "Yönetici bildirimi", invitation: "Randevu daveti" }[l.kind] || l.kind} · {fmt(l.at)} {l.ok ? "" : `(${l.detail})`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  useAdminTitle("Başvurular");
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(location.state?.user || null);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data: u }) => setMe(u)).catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);

  const load = useCallback(async (s, query) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/applications", { params: { status: s || undefined, q: query || undefined } });
      setData({ items: Array.isArray(d?.items) ? d.items : [], counts: d?.counts ?? {} });
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin", { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!me) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(status, q), 250);
    return () => clearTimeout(timer.current);
  }, [me, status, q, load]);

  const logout = async () => { await api.post("/auth/logout").catch(() => {}); navigate("/admin", { replace: true }); };
  const onChange = (app) => { setSelected(app); load(status, q); };
  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-ivory"><Loader2 className="animate-spin text-forest" /></div>;

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-forest/10 bg-forest text-ivory">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3"><LogoImg light className="h-10 w-auto" /><span className="font-display text-xl">Yönetim paneli</span></div>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden text-ivory/80 sm:inline" data-testid="admin-user-email">{me.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-ivory/25 px-4 py-2 hover:bg-ivory/10"><LogOut size={15} /> Çıkış</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-6 px-5 py-8">
        <BookingSettings />
        <div className="flex flex-wrap gap-2" data-testid="status-filters">
          <button onClick={() => setStatus("")} data-testid="filter-all" className={`rounded-full px-4 py-2 text-sm ${!status ? "bg-forest text-ivory" : "bg-white text-forest"}`}>Tümü ({total})</button>
          {Object.entries(STATUS).map(([k, [l]]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`filter-${k}`} className={`rounded-full px-4 py-2 text-sm ${status === k ? "bg-forest text-ivory" : "bg-white text-forest"}`}>{l} ({data.counts[k] ?? 0})</button>
          ))}
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-forest/15 bg-white px-4 py-2.5">
          <Search size={16} className="text-forest/60" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad veya e-posta ile ara" className="flex-1 bg-transparent text-sm outline-none" />
        </label>
        <div className="overflow-hidden rounded-2xl border border-forest/10 bg-white">
          {loading ? (
            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-forest" /></div>
          ) : data.items.length === 0 ? (
            <p className="p-10 text-center text-sm text-forest/70" data-testid="applications-empty">Henüz başvuru yok.</p>
          ) : (
            <ul className="divide-y divide-forest/10" data-testid="applications-list">
              {data.items.map((a) => (
                <li key={a.id}>
                  <button onClick={() => setSelected(a)} data-testid={`application-row-${a.id}`} className="grid w-full gap-2 px-5 py-4 text-left transition-colors hover:bg-ivory md:grid-cols-12 md:items-center">
                    <span className="md:col-span-3"><span className="block font-medium text-forest">{a.full_name}</span><span className="text-xs text-forest/65">{fmt(a.created_at)}</span></span>
                    <span className="flex items-center gap-1.5 truncate text-sm text-forest/80 md:col-span-3"><Mail size={13} /> {a.email}{a.phone && <><Phone size={13} className="ml-2" /> {a.phone}</>}</span>
                    <span className="text-sm text-forest/80 md:col-span-3">{A.location[a.location]} · {A.urgency[a.urgency]}</span>
                    <span className="flex items-center gap-2 md:col-span-3 md:justify-end"><span className="text-xs text-forest/65">{A.pay[a.willing_to_pay]?.split(",")[0]}</span><Badge s={a.status} /></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto bg-ivory sm:max-w-xl">
          {selected && (
            <>
              <SheetHeader className="mb-6 text-left">
                <SheetTitle className="font-display text-3xl text-forest">{selected.full_name}</SheetTitle>
                <SheetDescription><Badge s={selected.status} /></SheetDescription>
              </SheetHeader>
              <Detail app={selected} onChange={onChange} />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
